import { useMemo, useState } from 'react'
import { Compass, ChevronLeft, ChevronRight } from 'lucide-react'
import Spinner from './Spinner'
import TaskEventReplay from './TaskEventReplay'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { useGetProjectExplorationsQuery, useGetProjectQuery } from '../services/projectDataApi'
import { projectPause } from '../lib/projectPause'
import './OverviewExplorations.css'

// The rrweb replay for the active / most recent exploration on the (experimental) project overview,
// with prev/next to cycle through every exploration. Explorations are newest-first in the store
// (backend orders by created_at DESC), so index 0 is the active/most-recent one. A pure consumer:
// ProjectOverview owns dispatching fetchExplorations (and polling), this just renders the list.
// `explorationIds` scopes the player to a specific set of explorations (the test-run detail page
// passes the ids belonging to that run); omitted, it shows every exploration in the project.
export default function OverviewExplorations({
  projectId,
  explorationIds,
}: {
  projectId: string
  explorationIds?: string[]
}) {
  const explorationsQuery = useGetProjectExplorationsQuery({ projectId, page: 1, pageSize: 100 })
  // A paused project has nothing running, so an exploration frozen at 'in-progress' must not drive the
  // player's live mode — it would autoplay a tail that never grows and caption the empty stage with
  // "QA is exploring your app right now".
  const pause = projectPause(useGetProjectQuery(projectId).data)
  const allExplorations = explorationsQuery.data?.items ?? []
  const loading = explorationsQuery.isLoading
  const [idx, setIdx] = useState(0)
  const explorations = useMemo(
    () => (explorationIds ? allExplorations.filter(e => explorationIds.includes(e.id)) : allExplorations),
    [allExplorations, explorationIds],
  )

  // A caller that scoped this to an empty set (a test run that ran no explorations) has already told
  // us there is nothing coming, so skip the reservation below: holding a stage open and then removing
  // it once the fetch lands is the more jarring of the two — it yanks everything below UPWARD.
  if (explorationIds && explorationIds.length === 0) return null

  // Reserve the section's footprint (head + stage + control bar) while explorations are still loading,
  // so the overview content below settles into its final position up front. Otherwise, when the
  // remaining data loads first, this section is absent (null); the replay then arrives and shifts
  // everything down, which the journey cells' `layout` animation renders as a jarring
  // downward slide. Only null out once loading has finished and there genuinely are no explorations.
  if (explorations.length === 0) {
    if (!loading) return null
    return (
      <section className="ov-explore" data-testid="overview-explorations" aria-busy="true">
        <div className="ov-explore__head">
          <span className="ov-explore__title">
            <Compass size={18} strokeWidth={1.75} aria-hidden /> Exploration
            <Spinner size={13} className="ov-explore__spin" />
          </span>
        </div>
        <div className="ov-explore__stage ov-explore__stage--placeholder" data-testid="overview-explorations-placeholder">
          <div className="ov-explore__stage-frame" />
          <div className="ov-explore__stage-bar" />
        </div>
      </section>
    )
  }

  const clamped = Math.min(idx, explorations.length - 1)
  const current = explorations[clamped]!
  const running = !pause && (current.status === 'in-progress' || current.status === 'queued')

  return (
    <section className="ov-explore" data-testid="overview-explorations">
      <div className="ov-explore__head">
        <span className="ov-explore__title">
          <Compass size={18} strokeWidth={1.75} aria-hidden /> Exploration
        </span>
        {explorations.length > 1 && (
          <div className="ov-explore__nav" data-testid="overview-explorations-nav">
            <button
              type="button"
              className="ov-explore__nav-btn"
              onClick={() => setIdx(Math.max(0, clamped - 1))}
              disabled={clamped === 0}
              aria-label="Newer exploration"
              data-testid="overview-explorations-prev"
              data-analytics-event="new-overview-exploration-prev"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="ov-explore__count" data-testid="overview-explorations-count">
              {clamped + 1} / {explorations.length}
            </span>
            <button
              type="button"
              className="ov-explore__nav-btn"
              onClick={() => setIdx(Math.min(explorations.length - 1, clamped + 1))}
              disabled={clamped === explorations.length - 1}
              aria-label="Older exploration"
              data-testid="overview-explorations-next"
              data-analytics-event="new-overview-exploration-next"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        )}
      </div>
      <div className="ov-explore__stage">
        <TaskEventReplay
          key={current.id}
          source={{ explorationId: current.id }}
          taskHref={taskDetailUrl(projectId, current.id)}
          active
          live={running}
        />
      </div>
      {current.prompt && <p className="ov-explore__prompt" title={current.prompt}>{current.prompt}</p>}
    </section>
  )
}
